/// <amd-module name="Release/Landing/LeftPanel/ActivePipelines/ActivePipelines" />
import * as React from "react";


import { VssComponent, IVssComponentProperties, IVssComponentContext } from "VSS/Platform/Layout";
import { IVssPageContext } from "VSS/Platform/Context";

import { getTFSPageData } from "Tfs/Platform/Page";

import { IFavoritesService } from "Favorites/Common/FavoritesService";
import { Favorite } from "Favorites/Common/Contracts/Favorite";

import { ReleaseDefinition, DeploymentStatus, EnvironmentStatus, DeploymentOperationStatus } from "Release/Client/Contracts/Release";
import { ActionCreatorManager } from "DistributedTask/Flux/ActionCreatorManager";
import { StoreManager } from "DistributedTask/Flux/StoreManager";
import { ActionsHubManager } from "DistributedTask/Flux/ActionsHubManager";

import { releasePipelineArtifactType } from "Release/Landing/LeftPanel/ActivePipelines/ActivePipelineFavoritesStar";
import { IActiveDefinitionReference } from "Release/Landing/DataProviderContracts";
import { SelectionActions } from "Release/Landing/Selection/SelectionActions";
import { getReleasesSignalRHub, IReleasesSignalRHandler } from "Release/Landing/SignalR/LandingSignalRHub";
import { ActivePipelinesActionCreator } from "Release/Landing/LeftPanel/ActivePipelines/ActivePipelinesActionCreator";
import { ActivePipelinesActions } from "Release/Landing/LeftPanel/ActivePipelines/ActivePipelinesActions";
import { ActivePipelinesStore } from "Release/Landing/LeftPanel/ActivePipelines/ActivePipelinesStore";
import { ActivePipelineList } from "Release/Landing/LeftPanel/ActivePipelines/ActivePipelineList";
import { getActiveDefinitionReferenceMatch } from "LandingUtils";
import { startScenario, PerfScenarios, endScenario } from "Release/Helpers/Performance";
import { MessageActions } from "HubMessageBar/MessageActions";
import { MessageBarKeys } from "Constants";

export class ActivePipelines extends VssComponent<IVssComponentProperties, {}>{
    constructor(props: IVssComponentProperties, context: IVssComponentContext) {
        super(props, context);

        this._actionCreator = ActionCreatorManager.GetActionCreator<ActivePipelinesActionCreator>(ActivePipelinesActionCreator);
        this._store = StoreManager.GetStore<ActivePipelinesStore>(ActivePipelinesStore);
        this._actions = ActionsHubManager.GetActionsHub<ActivePipelinesActions>(ActivePipelinesActions);

        this._favoritesService = context.pageContext.getService<IFavoritesService>("IFavoritesService");
        this._favoritesService.subscribe(this._onFavoriteAdded, "favoriteAdded");

        // Recent might be populated through selections in All tab. Passing it down
        this._actionCreator.initializeDefs(context.pageContext, this._store.getState().recentDefinitions || [], this._store.getState().searchText);
        this._store.addChangedListener(this._onStoreChange);

        startScenario(this.context.pageContext, PerfScenarios.ActivePivot, false);
    }

    public componentWillUnmount(): void {
        this._store.removeChangedListener(this._onStoreChange);
        this._favoritesService.unsubscribe(this._onFavoriteAdded, "favoriteAdded");
        this._unsubscribeFromAllPipelines();

        const signalRHub = getReleasesSignalRHub();
        if (signalRHub) {
            signalRHub.removeSignalRHanlder(this._signalRHandler);
        }
    }

    public componentDidMount(): void {
        endScenario(this.context.pageContext, PerfScenarios.ActivePivot, false);

        this._signalRHandler = new ActivePipelinesSignalRHandler(this.context.pageContext, this._actions, this._actionCreator, this._store);
        const signalRHub = getReleasesSignalRHub();
        if (signalRHub) {
            signalRHub.addSignalRHanlder(this._signalRHandler);
            this._subscribeToPipelines();
        }
    }

    public componentDidUpdate(): void {
        this._subscribeToPipelines();
    }

    public render(): React.ReactNode {

        const state = this._store.getState();

        const onRowClick = (id: number, name: string) => {
            const selectionActions = ActionsHubManager.GetActionsHub<SelectionActions>(SelectionActions);
            if (state.searchText) {
                const definition = state.searchedDefinitions && state.searchedDefinitions.find((rd: ReleaseDefinition) => rd.id === id);
                if (definition) {
                    selectionActions.selectDefinition.invoke({
                        id: id,
                        noSelection: false,
                        folderId: ""
                    });

                    // We need to check whether this selection from searched RDs is present in active or recent
                    // If not, then we'll need to get the last deployent against this RD, and add it to Recent
                    const existingMatchInMine = getActiveDefinitionReferenceMatch(state.activeDefinitions, id) || getActiveDefinitionReferenceMatch(state.recentDefinitions, id);
                    if (!existingMatchInMine) {
                        this._actionCreator.handleAdditionalDefSelection(this.context.pageContext, definition, undefined);
                    }
                }
            }
            else {
                let definition: IActiveDefinitionReference | undefined = getActiveDefinitionReferenceMatch(state.activeDefinitions, id);

                if (!definition) {
                    definition = getActiveDefinitionReferenceMatch(state.recentDefinitions, id);
                }

                if (definition) {
                    selectionActions.selectDefinition.invoke({
                        id: id,
                        noSelection: false,
                        folderId: ""
                    });
                }
            }

            // Clear message bar error on selection change as we are canging context
            ActionsHubManager.GetActionsHub<MessageActions>(MessageActions).setMessage.invoke({ key: MessageBarKeys.HubError, value: "" });
            ActionsHubManager.GetActionsHub<MessageActions>(MessageActions).setMessage.invoke({ key: MessageBarKeys.HubSuccess, value: "" });
        };

        return (<ActivePipelineList
            activeDefinitions={state.activeDefinitions}
            recentDefinitions={state.recentDefinitions}
            searchText={state.searchText}
            searchedDefinitions={state.searchedDefinitions}
            searchResultsLoading={state.searchResultsLoading}
            onRowClick={onRowClick}
        />);
    }

    private _onStoreChange = (): void => {
        this.setState({});
    }

    private _onFavoriteAdded = (favorite: Favorite): void => {
        if (favorite.artifactType === releasePipelineArtifactType) {
            const state = this._store.getState();
            const definitionId = parseInt(favorite.artifactId, 10);
            const activeMatch = getActiveDefinitionReferenceMatch(state.activeDefinitions, definitionId);
            if (activeMatch) {
                return;
            }

            const recentMatch = getActiveDefinitionReferenceMatch(state.recentDefinitions, definitionId);
            if (recentMatch) {
                // In case of a pipeline being favorited from the menu item in all, the pipeline has to be first selected
                // This would ensure that the pipeline is added to recent already, as a selection in all adds an entry to recent
                this._actions.onRecentFavorited.invoke(definitionId);
            }
            else {
                const definition = state.searchedDefinitions && state.searchedDefinitions.find((rd: ReleaseDefinition) => rd.id === definitionId);
                if (definition) {
                    this._actionCreator.handleOnFavoriteReleaseDefinition(this.context.pageContext, definition);
                }
            }
        }
    }

    private _subscribeToPipelines(): void {
        const state = this._store.getState();
        const pipelinesToWatch = [...(state.activeDefinitions || []), ...(state.recentDefinitions || [])].map(def => def.id);;

        if (pipelinesToWatch) {
            const signalRHub = getReleasesSignalRHub()
            const tfsPageData = getTFSPageData(this.context.pageContext);

            // SingalR hub handler will ensure that if a definition is already being watched, it will not watch it again
            if (signalRHub && tfsPageData && tfsPageData.project) {
                signalRHub.watchReleaseDefinitionsReleases(tfsPageData.project.id, pipelinesToWatch);
            }
        }
    }

    private _unsubscribeFromAllPipelines(): void {
        const signalRHub = getReleasesSignalRHub()
        const tfsPageData = getTFSPageData(this.context.pageContext);
        if (signalRHub && tfsPageData && tfsPageData.project) {
            signalRHub.stopWatchingAllPipelines(tfsPageData.project.id);
        }
    }

    private _signalRHandler: ActivePipelinesSignalRHandler;
    private _store: ActivePipelinesStore;
    private _actionCreator: ActivePipelinesActionCreator;
    private _actions: ActivePipelinesActions;
    private _favoritesService: IFavoritesService;
}

class ActivePipelinesSignalRHandler implements IReleasesSignalRHandler {
    constructor(
        private _pageContext: IVssPageContext,
        private _actions: ActivePipelinesActions,
        private _actionCreator: ActivePipelinesActionCreator,
        private _store: ActivePipelinesStore) { }

    public onReleaseEnvironmentStatus(definitionId: number, releaseId: number, environmentId: number, environmentStatus: EnvironmentStatus, deploymentStatus: DeploymentStatus, operationStatus: DeploymentOperationStatus): void {
        const state = this._store.getState();
        const relevantPipelines = [...(state.activeDefinitions || []), ...(state.recentDefinitions || [])];
        if (relevantPipelines.some(pipe => pipe.id === definitionId)) {
            this._actions.updateReleaseEnvironmentStatus.invoke({
                definitionId: definitionId,
                releaseId: releaseId,
                releaseEnvironmentId: environmentId,
                deploymentStatus: deploymentStatus,
                operationStatus: operationStatus
            });
        }
    }

    public onReleaseCreated(definitionId: number, releaseId: number): void {
    }

    public onDeployJobStartedEvent(definitionId: number, releaseId: number, definitionEnvironmentId: number, releaseEnvironmentId: number): void {
        const state = this._store.getState();
        const relevantPipelines = [...(state.activeDefinitions || []), ...(state.recentDefinitions || [])];
        if (relevantPipelines.some(pipe => pipe.id === definitionId)) {
            this._actionCreator.handleDeploymentStarted(this._pageContext, definitionId, releaseId, releaseEnvironmentId);
        }
    }

    public onReleaseApprovalPending(definitionId: number, releaseId: number, releaseEnvironmentId: number, approvalId: number, approverId: string): void {
        const state = this._store.getState();
        const relevantPipelines = [...(state.activeDefinitions || []), ...(state.recentDefinitions || [])];
        const matchingPipeline = relevantPipelines.find(pipe => pipe.id === definitionId);
        if (matchingPipeline) {
            if (matchingPipeline.lastDeployment && matchingPipeline.lastDeployment.releaseEnvironment.id === releaseEnvironmentId) {
                this._actions.updateReleaseEnvironmentStatus.invoke({
                    definitionId: definitionId,
                    releaseId: releaseId,
                    releaseEnvironmentId: releaseEnvironmentId,
                    deploymentStatus: DeploymentStatus.InProgress,
                    operationStatus: DeploymentOperationStatus.Pending
                });
            }
            else {
                this._actionCreator.handleDeploymentStarted(this._pageContext, definitionId, releaseId, releaseEnvironmentId);
            }
        }
    }
}